import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import { TabView, TabPanel } from 'primereact/tabview';
import { createPurchaseOrder, getClients, getProducts } from '../api';
import { Client } from '../types/client';
import { Product } from '../types/product';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { HandlingUnitStage } from '../types/purchaseOrder';
import { AxiosError } from 'axios';
import PdfUpload from './PdfUpload';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/user';
import { parseDateOnly, formatDateOnly } from '../utils';

interface Item {
  itemNumber: string;
  material: string;
  displayName: string;
  quantity: string;
  shipToParty: string;
  batchNumber: string;
  deliveryDate: string;
  hu: string[];
  stage: HandlingUnitStage;
  unitPrice: string;
}

const CreatePurchaseOrder: React.FC = () => {
  const [poNumber, setPoNumber] = useState('');
  const [clientId, setClientId] = useState<number | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [newHUs, setNewHUs] = useState<{ [key: number]: string }>({});
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [productInputs, setProductInputs] = useState<{ [key: number]: string }>({});
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // For customer users, automatically set their client ID
    if (user?.role === UserRole.Customer && user?.client?.id) {
      setClientId(user.client.id);
    }

    const loadClients = async () => {
      try {
        const clientsData = await getClients();
        setClients(clientsData);
      } catch (error) {
        console.error('Error loading clients:', error);
        addNotification('error', 'Failed to load clients');
      }
    };

    // Only load clients for admin/manager users
    if (user?.role !== UserRole.Customer) {
      loadClients();
    }
  }, [addNotification, user]);

  const searchProducts = async (event: { query: string }) => {
    try {
      const { data } = await getProducts({
        search: event.query,
        page: 1,
        limit: 10,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      setFilteredProducts(data);
    } catch (error) {
      console.error('Error searching products:', error);
      setFilteredProducts([]);
    }
  };

  const handleSubmit = async () => {
    if (!poNumber || !clientId || items.length === 0) {
      addNotification('error', 'Please fill in all required fields');
      return;
    }

    // Validate that all items have a product selected
    const invalidItems = items.filter(item => !item.material);
    if (invalidItems.length > 0) {
      addNotification('error', 'Please select a product for all items');
      return;
    }

    try {
      setLoading(true);
      const purchaseOrder = await createPurchaseOrder({
        poNumber,
        clientId,
        items: items.map(item => ({
          itemNumber: item.itemNumber,
          material: item.material,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          unit: 'EA',
          currency: 'USD',
          shipToParty: item.shipToParty,
          batchNumber: item.batchNumber,
          deliveryDate: item.deliveryDate,
          hu: item.hu,
          stage: HandlingUnitStage.Pending
        })),
        notes,
      });

      addNotification('success', 'Purchase order created successfully');
      navigate(`/purchase-orders/${purchaseOrder.id}`);
    } catch (error) {
      console.error('Error creating purchase order:', error);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          // Handle duplicate PO number error
          addNotification('error', `Purchase Order number ${poNumber} already exists. Please use a different number.`);
        } else if (error.response?.data?.message) {
          addNotification('error', error.response.data.message);
        } else {
          addNotification('error', 'Failed to create purchase order');
        }
      } else {
        addNotification('error', 'Failed to create purchase order');
      }
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    const newIndex = items.length;
    const itemNumber = (newIndex * 10 + 10).toString();
    setItems([
      ...items,
      {
        itemNumber,
        material: '',
        displayName: '',
        quantity: '',
        shipToParty: '',
        batchNumber: '',
        stage: HandlingUnitStage.Pending,
        deliveryDate: '',
        hu: [],
        unitPrice: '',
      },
    ]);
    setNewHUs({ ...newHUs, [newIndex]: '' });
    setProductInputs(prev => ({ ...prev, [newIndex]: '' }));
  };

  const updateItem = (index: number, field: keyof Item, value: any) => {
    if (field === 'material') {
      if (typeof value === 'string') {
        // User is typing
        setProductInputs(prev => ({ ...prev, [index]: value }));
      } else if (typeof value === 'object' && value !== null) {
        // User selected a product from suggestions
        const updatedItems = [...items];
        updatedItems[index] = {
          ...updatedItems[index],
          material: value.id.toString(),
          displayName: value.name,
          unitPrice: value.basePrice.toString(),
        };
        setItems(updatedItems);
        setProductInputs(prev => ({ ...prev, [index]: value.name }));
      }
    } else {
      // Handle other fields normally
      const updatedItems = [...items];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
      setItems(updatedItems);
    }
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    const updatedNewHUs = { ...newHUs };
    delete updatedNewHUs[index];
    setNewHUs(updatedNewHUs);
  };

  const addHandlingUnit = (index: number) => {
    const huNumber = newHUs[index];
    if (!huNumber?.trim()) return;

    const updatedItems = [...items];
    updatedItems[index].hu.push(huNumber.trim());
    setItems(updatedItems);
    setNewHUs({ ...newHUs, [index]: '' });
  };

  const removeHandlingUnit = (itemIndex: number, huIndex: number) => {
    const updatedItems = [...items];
    updatedItems[itemIndex].hu = updatedItems[itemIndex].hu.filter((_, i) => i !== huIndex);
    setItems(updatedItems);
  };

  const renderHandlingUnits = (item: Item, itemIndex: number) => {
    return (
      <div>
        <DataTable value={item.hu.map(hu => ({ huNumber: hu }))}>
          <Column field="huNumber" header="HU Number" />
          <Column
            body={(_, { rowIndex }) => (
              <Button
                icon="pi pi-trash"
                className="p-button-danger p-button-sm"
                onClick={() => removeHandlingUnit(itemIndex, rowIndex)}
              />
            )}
          />
        </DataTable>
        <div className="flex mt-2">
          <InputText
            placeholder="New HU Number"
            value={newHUs[itemIndex] || ''}
            onChange={(e) => setNewHUs({ ...newHUs, [itemIndex]: e.target.value })}
            className="mr-2"
          />
          <Button
            icon="pi pi-plus"
            onClick={() => addHandlingUnit(itemIndex)}
            className="p-button-sm"
          />
        </div>
      </div>
    );
  };

  return (
    <Card title="Create Purchase Order">
      <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header="Manual Entry">
          <div className="p-fluid grid">
            <div className="field col-12 md:col-6">
              <label htmlFor="poNumber">PO Number</label>
              <InputText
                id="poNumber"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
              />
            </div>

            {user?.role !== UserRole.Customer && (
              <div className="field col-12 md:col-6">
                <label htmlFor="client">Client</label>
                <select
                  id="client"
                  className="p-inputtext p-component"
                  value={clientId || ''}
                  onChange={(e) => setClientId(parseInt(e.target.value))}
                >
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="field col-12">
              <label htmlFor="notes">Notes</label>
              <InputText
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="field col-12">
              <h3>Items</h3>
              <DataTable value={items} className="mb-3">
                <Column
                  field="displayName"
                  header="Product"
                  body={(_, { rowIndex }) => (
                    <AutoComplete
                      value={productInputs[rowIndex] || ''}
                      suggestions={filteredProducts}
                      completeMethod={searchProducts}
                      field="name"
                      onChange={(e) => updateItem(rowIndex, 'material', e.value)}
                      forceSelection={false}
                    />
                  )}
                />
                <Column
                  field="quantity"
                  header="Quantity"
                  body={(_, { rowIndex }) => (
                    <InputText
                      value={items[rowIndex].quantity}
                      onChange={(e) => updateItem(rowIndex, 'quantity', e.target.value)}
                      type="number"
                    />
                  )}
                />
                <Column
                  field="shipToParty"
                  header="Ship To"
                  body={(_, { rowIndex }) => (
                    <InputText
                      value={items[rowIndex].shipToParty}
                      onChange={(e) => updateItem(rowIndex, 'shipToParty', e.target.value)}
                    />
                  )}
                />
                <Column
                  field="batchNumber"
                  header="Batch"
                  body={(_, { rowIndex }) => (
                    <InputText
                      value={items[rowIndex].batchNumber}
                      onChange={(e) => updateItem(rowIndex, 'batchNumber', e.target.value)}
                    />
                  )}
                />
                <Column
                  field="deliveryDate"
                  header="Delivery Date"
                  body={(_, { rowIndex }) => (
                    <Calendar
                      value={parseDateOnly(items[rowIndex].deliveryDate)}
                      onChange={(e) => {
                        const date = e.value as Date;
                        if (date) {
                          updateItem(rowIndex, 'deliveryDate', formatDateOnly(date));
                        }
                      }}
                      dateFormat="yy-mm-dd"
                    />
                  )}
                />
                <Column
                  field="hu"
                  header="Handling Units"
                  body={(item, { rowIndex }) => renderHandlingUnits(item, rowIndex)}
                />
                <Column
                  body={(_, { rowIndex }) => (
                    <Button
                      icon="pi pi-trash"
                      className="p-button-danger p-button-sm"
                      onClick={() => removeItem(rowIndex)}
                    />
                  )}
                />
              </DataTable>
            </div>

            <div className="field col-12">
              <Button
                label="Add Item"
                icon="pi pi-plus"
                onClick={addItem}
                className="p-button-sm mb-3"
              />
            </div>

            <div className="field col-12">
              <Button
                label="Create Purchase Order"
                onClick={handleSubmit}
                loading={loading}
                className="p-button-success"
              />
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Upload PDF">
          <div className="p-fluid">
            <div className="field">
              <PdfUpload />
            </div>
          </div>
        </TabPanel>
      </TabView>
    </Card>
  );
};

export default CreatePurchaseOrder;
